import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Loader from '../../../components/Loader'
import Toast from '../../../components/Toast/Toast'
import CustomModal from '../../../components/Modals/CustomModal'
import Swal from 'sweetalert2'
import { PRIVATE_ROUTES } from '../../../Routes/routes'
import {
  deleteContractDocument,
  getContractDocumentById,
  updateContractDocument,
} from '../../../services/NewServicesCompnay'
import { TAGS } from '../../../services/Tags'

const ACCENT = '#01A76A'

const cardStyle = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
}

const parseBoolText = (value) => String(value).toLowerCase() === 'true'

const toFormString = (value) =>
  value == null || value === '' ? '' : String(value)

const trimFormValue = (value) => toFormString(value).trim()

const StatusBadge = ({ active }) => (
  <span
    className="badge"
    style={{
      background: active ? '#dcfce7' : '#fee2e2',
      color: active ? '#166534' : '#991b1b',
      fontSize: 12,
      fontWeight: 600,
      padding: '5px 10px',
    }}
  >
    {active ? 'Activo' : 'Inactivo'}
  </span>
)

const FlagBadge = ({ label, on }) => (
  <span
    style={{
      fontSize: 12,
      fontWeight: 500,
      padding: '4px 10px',
      borderRadius: 6,
      background: on ? '#eff6ff' : '#f3f4f6',
      color: on ? '#1d4ed8' : '#6b7280',
    }}
  >
    {label}: {on ? 'Sí' : 'No'}
  </span>
)

const infoGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 20,
}

const InfoField = ({ label, value, highlight }) => (
  <div
    style={{
      padding: '20px 24px',
      background: highlight ? '#f0fdf4' : '#f9fafb',
      borderRadius: 10,
      border: highlight ? '1px solid #bbf7d0' : '1px solid #f3f4f6',
      minHeight: 92,
    }}
  >
    <div
      style={{
        fontSize: 11,
        color: '#6b7280',
        marginBottom: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: highlight ? 20 : 16,
        color: '#111827',
        fontWeight: 700,
        lineHeight: 1.3,
        wordBreak: 'break-word',
      }}
    >
      {value}
    </div>
  </div>
)

const SectionTitle = ({ children }) => (
  <h2
    className="text-on-bg-primary mb-0"
    style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}
  >
    {children}
  </h2>
)

const ContractDocumentDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState('')
  const [taxes, setTaxes] = useState('')
  const [amount, setAmount] = useState('')
  const [months, setMonths] = useState('')
  const [taxApplies, setTaxApplies] = useState(false)
  const [indefiniteTime, setIndefiniteTime] = useState(false)

  const { data: document, isLoading, isError } = useQuery(
    [TAGS.contract_document_detail, id],
    () => getContractDocumentById(id),
    {
      enabled: Boolean(id),
      select: (data) => data?.res?.document ?? null,
    }
  )

  const syncFormFromDocument = useCallback((doc) => {
    if (!doc) return
    const extra = doc.extra ?? {}
    setName(toFormString(doc.name))
    setTaxes(toFormString(extra.taxes))
    setAmount(toFormString(extra.amount))
    setMonths(toFormString(extra.months))
    setTaxApplies(parseBoolText(extra.taxApplies))
    setIndefiniteTime(parseBoolText(extra.indefiniteTime))
  }, [])

  useEffect(() => {
    if (!document || isEditing) return
    syncFormFromDocument(document)
  }, [document, isEditing, syncFormFromDocument])

  const { mutate: updateDocument, isLoading: isUpdating } = useMutation(
    updateContractDocument,
    {
      onSuccess: ({ response, res }) => {
        if (!response?.ok) {
          Toast({
            icon: 'error',
            title: res?.msg || res?.error || 'No se pudo actualizar el contrato',
          })
          return
        }

        queryClient.invalidateQueries([TAGS.contract_documents])
        queryClient.invalidateQueries([TAGS.contract_document_detail, id])
        queryClient.invalidateQueries(['contract-documents'])
        Toast({ title: 'Contrato actualizado correctamente' })
        setIsEditing(false)
      },
      onError: () => {
        Toast({ icon: 'error', title: 'No se pudo actualizar el contrato' })
      },
    }
  )

  const { mutate: removeDocument, isLoading: isDeleting } = useMutation(
    deleteContractDocument,
    {
      onSuccess: ({ response, res }) => {
        if (!response?.ok) {
          Toast({
            icon: 'error',
            title: res?.msg || res?.error || 'No se pudo eliminar el contrato',
          })
          return
        }

        queryClient.invalidateQueries([TAGS.contract_documents])
        queryClient.invalidateQueries(['contract-documents'])
        Swal.close()
        Toast({ title: 'Contrato eliminado correctamente' })
        navigate(PRIVATE_ROUTES.master_contract_documents)
      },
      onError: () => {
        Toast({ icon: 'error', title: 'No se pudo eliminar el contrato' })
      },
    }
  )

  const handleDelete = () => {
    CustomModal({
      title: 'Eliminar contrato',
      description: `¿Seguro que deseas eliminar "${document?.name}"? Esta acción no se puede deshacer.`,
      textPrimaryButton: 'Eliminar',
      textSecondaryButton: 'Cancelar',
      handlePrimaryButton: () => removeDocument(id),
      handleSecondaryButton: () => Swal.close(),
    })
  }

  const handleCancelEdit = () => {
    syncFormFromDocument(document)
    setIsEditing(false)
  }

  const handleSave = (e) => {
    e?.preventDefault?.()

    const nameStr = trimFormValue(name)
    const taxesStr = trimFormValue(taxes)
    const amountStr = trimFormValue(amount)
    const monthsStr = trimFormValue(months)

    if (!nameStr || !taxesStr || !amountStr) {
      Toast({ icon: 'error', title: 'Completa los campos obligatorios' })
      return
    }

    if (!indefiniteTime && !monthsStr) {
      Toast({ icon: 'error', title: 'Indica la duración en meses o marca tiempo indefinido' })
      return
    }

    updateDocument({
      id,
      name: nameStr,
      content: '',
      taxes: taxesStr,
      amount: amountStr,
      taxApplies,
      indefiniteTime,
      months: indefiniteTime ? 0 : monthsStr,
    })
  }

  const handleStartEdit = (e) => {
    e.preventDefault()
    e.stopPropagation()
    syncFormFromDocument(document)
    setIsEditing(true)
  }

  if (isLoading) {
    return (
      <DashboardWrapper showTitle title="Detalle del contrato">
        <Loader />
      </DashboardWrapper>
    )
  }

  if (isError || !document) {
    return (
      <DashboardWrapper showTitle title="Detalle del contrato">
        <p className="text-on-bg-secondary">No se encontró el contrato.</p>
        <Link to={PRIVATE_ROUTES.master_contract_documents}>Volver al listado</Link>
      </DashboardWrapper>
    )
  }

  const extra = document.extra ?? {}
  const monthsDisplay = parseBoolText(extra.indefiniteTime)
    ? 'Indefinido'
    : extra.months ?? '—'

  const createdAt = document.created_at
    ? dayjs(document.created_at).format('DD/MM/YYYY HH:mm')
    : '—'
  const updatedAt = document.updated_at
    ? dayjs(document.updated_at).format('DD/MM/YYYY HH:mm')
    : '—'

  return (
    <DashboardWrapper
      showTitle
      title="Detalle del contrato"
      dashboardWrapperClassName="w-100 flex-grow-1"
    >
      {/* Toolbar: navigation + actions */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4"
        style={{ rowGap: 12 }}
      >
        <Link
          to={PRIVATE_ROUTES.master_contract_documents}
          className="text-decoration-none d-inline-flex align-items-center gap-1"
          style={{ fontSize: 14, color: ACCENT, fontWeight: 500 }}
        >
          <span aria-hidden>←</span> Volver al listado
        </Link>

        <div
          className={`d-flex flex-wrap align-items-center gap-2 ms-md-auto ${
            isEditing ? 'w-100 w-md-auto' : ''
          }`}
          style={isEditing ? { maxWidth: 480 } : undefined}
        >
          {isEditing ? (
            <>
              <button
                type="button"
                className="btn btn-white border"
                onClick={handleCancelEdit}
                disabled={isUpdating}
              >
                Cancelar
              </button>
              <div className="flex-grow-1" aria-hidden />
              <button
                type="button"
                className="btn btn-bg-CTA-primary text-on-bg-primary"
                style={{ borderRadius: 8 }}
                onClick={handleSave}
                disabled={isUpdating}
              >
                {isUpdating ? <Loader /> : 'Guardar cambios'}
              </button>
            </>
          ) : (
            <>
              {document.file_url && (
                <a
                  href={document.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  Descargar Word
                </a>
              )}
              <button
                type="button"
                className="btn btn-sm btn-bg-CTA-primary text-on-bg-primary"
                style={{ borderRadius: 8 }}
                onClick={handleStartEdit}
              >
                Editar
              </button>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando…' : 'Eliminar'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Hero header */}
      <div
        className="mb-4"
        style={{
          background: `linear-gradient(135deg, ${ACCENT} 0%, #019960 100%)`,
          borderRadius: 14,
          padding: '14px 36px',
          color: '#fff',
        }}
      >
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-4">
          <div style={{ minWidth: 0, flex: '1 1 320px' }}>
            <p style={{ margin: 0, fontSize: 12, opacity: 0.85 }}>
              Contrato #{document.id}
            </p>
            <h1
              className="mb-3 mt-2"
              style={{
                fontSize: 'clamp(1.35rem, 2.5vw, 1.85rem)',
                fontWeight: 700,
                margin: 0,
                lineHeight: 1.25,
              }}
            >
              {document.name}
            </h1>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <StatusBadge active={document.active} />
              <FlagBadge label="Bloqueado" on={document.blocked} />
              <FlagBadge label="Impuestos" on={parseBoolText(extra.taxApplies)} />
              {parseBoolText(extra.indefiniteTime) && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    padding: '4px 10px',
                    borderRadius: 6,
                    background: 'rgba(255,255,255,0.2)',
                  }}
                >
                  Duración indefinida
                </span>
              )}
            </div>
          </div>

          {!isEditing && (
            <div
              className="d-flex flex-wrap gap-4"
              style={{
                flex: '0 1 auto',
                fontSize: 13,
                opacity: 0.92,
                paddingTop: 4,
              }}
            >
              <div>
                <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 4 }}>Creado</div>
                <div style={{ fontWeight: 600 }}>{createdAt}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 4 }}>Actualizado</div>
                <div style={{ fontWeight: 600 }}>{updatedAt}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ ...cardStyle, padding: '32px 36px', width: '100%' }}>
        <SectionTitle>
          {isEditing ? 'Editar contrato' : 'Detalle del contrato'}
        </SectionTitle>

        {isEditing ? (
          <form id="contract-edit-form" onSubmit={handleSave} className="mt-4">
            <div className="row g-4">
              <div className="col-12">
                <label className="form-label fw-bold mb-2" style={{ fontSize: 13 }}>
                  Nombre *
                </label>
                <input
                  type="text"
                  className="form-control form-control-lg"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6 col-xl-3">
                <label className="form-label fw-bold mb-2" style={{ fontSize: 13 }}>
                  Impuestos (%) *
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={taxes}
                  onChange={(e) => setTaxes(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6 col-xl-3">
                <label className="form-label fw-bold mb-2" style={{ fontSize: 13 }}>
                  Monto *
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6 col-xl-3">
                <label className="form-label fw-bold mb-2" style={{ fontSize: 13 }}>
                  Meses
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={months}
                  onChange={(e) => setMonths(e.target.value)}
                  disabled={indefiniteTime}
                  placeholder={indefiniteTime ? 'Indefinido' : ''}
                />
              </div>
              <div className="col-12 col-xl-3 d-flex align-items-end pb-1">
                <div className="d-flex flex-column flex-sm-row flex-xl-column gap-3 w-100 pt-xl-0 pt-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="edit-tax-applies"
                      checked={taxApplies}
                      onChange={(e) => setTaxApplies(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="edit-tax-applies">
                      Aplica impuestos
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="edit-indefinite-time"
                      checked={indefiniteTime}
                      onChange={(e) => setIndefiniteTime(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="edit-indefinite-time">
                      Tiempo indefinido
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className="mt-3 d-flex flex-column" style={{ gap: 20 }}>
            <div>
              <SectionTitle>Condiciones</SectionTitle>
              <div className="mt-4" style={infoGridStyle}>
                <InfoField
                  label="Impuestos"
                  value={`${extra.taxes ?? '—'}%`}
                  highlight
                />
                <InfoField label="Monto" value={extra.amount ?? '—'} highlight />
                <InfoField label="Duración" value={monthsDisplay} />
                <InfoField
                  label="Aplica impuestos"
                  value={parseBoolText(extra.taxApplies) ? 'Sí' : 'No'}
                />
                <InfoField
                  label="Tiempo indefinido"
                  value={parseBoolText(extra.indefiniteTime) ? 'Sí' : 'No'}
                />
              </div>
            </div>

            <div
              style={{
                height: 1,
                background: '#f3f4f6',
                margin: '0 -4px',
              }}
            />

            <div>
              <SectionTitle>Registro</SectionTitle>
              <div className="mt-4" style={infoGridStyle}>
                <InfoField label="ID" value={document.id} />
                <InfoField label="Estado" value={document.active ? 'Activo' : 'Inactivo'} />
                <InfoField label="Bloqueado" value={document.blocked ? 'Sí' : 'No'} />
                <InfoField label="Creado" value={createdAt} />
                <InfoField label="Actualizado" value={updatedAt} />
              </div>
            </div>

            {document.file_url && (
              <div
                className="d-flex flex-wrap align-items-center justify-content-between gap-3"
                style={{
                  padding: '20px 24px',
                  background: '#f0fdf4',
                  borderRadius: 10,
                  border: '1px solid #bbf7d0',
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                    Documento Word adjunto
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                    Descarga la plantilla asociada a este contrato.
                  </div>
                </div>
                <a
                  href={document.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary"
                >
                  Descargar Word
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardWrapper>
  )
}

export default ContractDocumentDetail
